<template>
  <CRow>
    <!-- ================= Confirm Assign Modal ================= -->
    <CModal title="Assign Institutes" :show.sync="confirmModal" :close-on-backdrop="false" color="warning">
      <p>
        You are assigning <strong>{{ selectedInstitutes.length }}</strong>
        institute{{ selectedInstitutes.length > 1 ? 's' : '' }} to this chain.
      </p>
      <p class="mb-0 text-danger">
        <strong>This cannot be undone.</strong>
        Members of these institutes are notified and invited straight away, so an assigned
        institute cannot be removed later.
      </p>
      <template #footer>
        <CButton color="secondary" @click="confirmModal = false">Cancel</CButton>
        <CButton color="warning" :disabled="saving" @click="submitData">
          {{ saving ? 'Assigning...' : 'Yes, assign' }}
        </CButton>
      </template>
    </CModal>

    <CCol col="12">
      <CCard>
        <CCardHeader>
          <div class="row">
            <CCol sm="6" class="align-self-center">
              <strong>Institute List</strong>
            </CCol>
          </div>
        </CCardHeader>
        <CCardBody>
          <CAlert :color="messageColor" closeButton :show.sync="dismissCountDownS" fade>
            {{ alertMessage }}
          </CAlert>

          <!-- Permanent note -->
          <CAlert color="info" class="mb-3">
            <strong>Note:</strong> Once an institute is ticked and saved, it is locked. Its members receive a
            join notification immediately, so the assignment cannot be changed or removed.
          </CAlert>

          <div class="d-flex align-items-center mb-3">
            <CButton
              size="sm"
              color="primary"
              :disabled="!selectedInstitutes.length || saving"
              @click="openConfirm"
            >
              <CIcon name="cil-check-circle" />
              Save Selected Institutes
            </CButton>
            <small class="ml-2 text-muted">
              {{ selectedInstitutes.length }} new institute{{ selectedInstitutes.length === 1 ? '' : 's' }} selected
            </small>
          </div>

          <v-server-table :columns="columns" :url="apiUrl" :options="options" ref="myTable">
            <template #assigned="data">
              <CBadge v-if="isAssigned(data.row)" color="success">Assigned</CBadge>
              <CBadge v-else-if="isSelected(data.row)" color="warning">Selected (not saved)</CBadge>
              <span v-else class="text-muted">-</span>
            </template>
            <template #actions="data">
              <CButton
                v-if="data.row.alumniAssociationId != undefined"
                color="warning"
                size="sm"
                title="View Member List"
                @click="viewTableRow(data.row.alumniAssociationId)"
              >
                <i class="fas fa-eye"></i>
              </CButton>
            </template>
          </v-server-table>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import Vue from "vue";
import {Mixin} from "/src/mixins/index.js";
import {ServerTable} from "vue-tables-2";
Vue.use(ServerTable, {}, false);

export default {
  name: "instituteChainInstitutes",
  mixins: [Mixin],
  data() {
    return {
      messageColor: "success",
      dismissCountDownS: 0,
      alertMessage: "",
      apiUrl: "",
      saving: false,
      confirmModal: false,

      // Only NEW selections (not yet assigned). Already-assigned rows are locked.
      selectedInstitutes: [],

      columns: ["checkbox", "name", "email", "assigned", "actions"],
      options: {
        headings: {
          checkbox: "",
          name: "Name",
          email: "Email",
          assigned: "Status",
          actions: "Action",
        },
        sortable: ["name", "email"],
        filterable: ["name", "email"],
        templates: {
          checkbox: (h, row) => {
            const locked = this.isAssigned(row);
            return h("input", {
              attrs: {
                name: "itemCheckbox",
                type: "checkbox",
                value: row.id,
                disabled: locked,
                title: locked ? "Already assigned - cannot be removed" : "",
              },
              domProps: {
                checked: locked || this.isSelected(row),
              },
              style: locked ? "cursor: not-allowed" : "cursor: pointer",
              on: {
                change: (event) => this.handleCheckboxChange(event, row),
              },
            });
          },
        },
      },
    };
  },
  created() {
    this.apiUrl = "/institute-chain/list-institute/" + this.$route.params.id;
  },
  methods: {
    /** Saved in institute_chain_colleges -> locked */
    isAssigned(row) {
      const v = row.checked;
      return v === true || v === 1 || v === "1" || v === "true";
    },
    isSelected(row) {
      return this.selectedInstitutes.includes(row.id);
    },
    handleCheckboxChange(event, row) {
      // Never allow un-ticking an assigned institute
      if (this.isAssigned(row)) {
        event.target.checked = true;
        return;
      }
      if (event.target.checked) {
        if (!this.isSelected(row)) this.selectedInstitutes.push(row.id);
      } else {
        this.selectedInstitutes = this.selectedInstitutes.filter((id) => id !== row.id);
      }
    },
    viewTableRow(item) {
      this.$router.push({path: `/alumni/view/member/${item}`});
    },
    showAlert(message, color = "success") {
      this.alertMessage = message;
      this.messageColor = color;
      this.dismissCountDownS = 10;
    },
    openConfirm() {
      if (!this.selectedInstitutes.length) return;
      this.confirmModal = true;
    },
    submitData() {
      if (!this.selectedInstitutes.length) return;
      this.saving = true;
      axios
        .post("institute-chain/assign-institute", {
          mainInstituteId: this.$route.params.id,
          collegeIds: this.selectedInstitutes,
        })
        .then((response) => {
          const d = response.data || {};
          if (d.error || d.status === false || d.success === false) {
            this.confirmModal = false;
            this.showAlert(d.error || d.message || "Failed to assign institutes", "danger");
            return;
          }
          // Done: close, clear the new selection, reload -> rows come back locked
          this.confirmModal = false;
          this.selectedInstitutes = [];
          this.showAlert(
            (typeof d.success === "string" && d.success) || d.message || "Institutes assigned successfully"
          );
          if (this.$refs.myTable) this.$refs.myTable.refresh();
        })
        .catch((error) => {
          this.confirmModal = false;
          const msg = error.response && error.response.data && (error.response.data.message || error.response.data.error);
          this.showAlert(msg || "Failed to assign institutes", "danger");
        })
        .finally(() => (this.saving = false));
    },
  },
};
</script>